// Mediterranean languages configuration for internationalization
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import language configuration
import { 
  MEDITERRANEAN_LANGUAGES, 
  DEFAULT_LANGUAGE, 
  LANGUAGE_DETECTION_ORDER, 
  LANGUAGE_FALLBACKS 
} from '../constants/languages';
import { SupportedLocale } from '../types';

// Translation namespaces for better organization
export const NAMESPACES = {
  COMMON: 'common',
  CODEX: 'codex', 
  TOOLBOX: 'toolbox',
  LOGBOOK: 'logbook',
  AUTH: 'auth',
} as const;

// Extract short language codes (e.g., 'it' from 'it-IT')
const getShortCode = (locale: string): string => {
  return locale.split('-')[0];
};

// Map full locales to short codes for file loading
const supportedLanguages = MEDITERRANEAN_LANGUAGES.map(lang => getShortCode(lang.code));
const uniqueSupportedLanguages = [...new Set(supportedLanguages)];

// Build version for cache busting (use package.json version or env variable)
const BUILD_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';

// i18next configuration following best practices
const i18nConfig = {
  // Keep i18next debug output opt-in; the default dev logger is too noisy for route-level smoke tests.
  debug: import.meta.env.DEV && import.meta.env.VITE_I18N_DEBUG === 'true',
  
  // Language settings. Do not set `lng` here: the browser detector should choose
  // the first anonymous language before falling back to English.
  fallbackLng: getShortCode(DEFAULT_LANGUAGE),
  supportedLngs: uniqueSupportedLanguages,
  
  // Namespace configuration
  defaultNS: NAMESPACES.COMMON,
  ns: [NAMESPACES.COMMON], // Start with common, load others on demand
  fallbackNS: NAMESPACES.COMMON,
  
  // Feature toggles
  react: {
    useSuspense: false, // Avoid route-level suspense deadlocks in production bundles
    bindI18n: 'languageChanged loaded',
    bindI18nStore: 'added removed',
    transEmptyNodeValue: '', // Return empty string for empty nodes
  },
  
  // Key handling - Following best practices
  keySeparator: '.', // Enable nested keys for better organization
  nsSeparator: ':',   // Namespace separator
  pluralSeparator: '_',
  contextSeparator: '_',
  
  // Loading strategy
  load: 'languageOnly' as const, // Load 'en' instead of 'en-GB'
  preload: [getShortCode(DEFAULT_LANGUAGE)], // Preload default language
  
  // Language detection settings
  detection: {
    // Simplified detection order
    order: ['localStorage', 'navigator', 'htmlTag'],
    
    // Detection settings
    lookupLocalStorage: 'i18nextLng',
    lookupSessionStorage: 'i18nextLng',
    lookupFromPathIndex: 0,
    lookupFromSubdomainIndex: 0,
    
    // Cache settings
    caches: ['localStorage'],
    excludeCacheFor: ['cimode'],
    
    // Only detect languages we support
    checkWhitelist: true,
    
    // Convert detected language to short code
    convertDetectedLanguage: (lng: string) => getShortCode(lng),
  },
  
  // Backend configuration for lazy loading
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
    // Add cache busting with version instead of timestamp
    queryStringParams: { v: BUILD_VERSION },
    crossDomain: false,
    withCredentials: false,
  },
  
  // Missing key behavior - single consolidated handler
  parseMissingKeyHandler: (key: string, defaultValue?: string) => {
    return defaultValue || key;
  },
  
  // Single missing key handler for cleaner logging
  saveMissing: false,
  updateMissing: false,
  missingKeyHandler: import.meta.env.DEV ? 
    (lngs: readonly string[], ns: string, key: string) => {
      // Throttle missing key warnings to prevent performance impact
      if (Math.random() < 0.1) { // Only log 10% of missing keys
        console.warn(`🌐 Missing translation: [${lngs[0]}] ${ns}:${key}`);
      }
    } : undefined,
  
  // Interpolation settings
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  
  // Return objects for complex translations
  returnObjects: true,
  returnEmptyString: false,
  returnNull: false,
  
  // Clean empty values from loaded translations
  cleanCode: true,
  
  // Non-essential missing keys handling
  nonExplicitSupportedLngs: false,
  nonExplicitWhitelist: false,
};

// Initialize i18next with plugins
const i18nInstance = i18next
  .use(Backend)           // Load translations from files
  .use(LanguageDetector)  // Detect user language
  .use(initReactI18next); // Initialize react-i18next

// Initialize with async/await to ensure proper loading
i18nInstance.init(i18nConfig, (err) => {
  if (err) {
    console.error('i18n initialization failed:', err);
  } else {
    console.log('i18n initialized successfully');
    document.documentElement.lang = getShortCode(i18next.resolvedLanguage || i18next.language || DEFAULT_LANGUAGE);
  }
});

// Language switching utility
export const changeLanguage = async (language: SupportedLocale): Promise<void> => {
  try {
    const shortCode = getShortCode(language);
    await i18next.changeLanguage(shortCode);
    await i18next.loadNamespaces(Object.values(NAMESPACES));
    // Store full locale preference
    localStorage.setItem('selectedLocale', language);
    localStorage.setItem('i18nextLng', shortCode);
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = shortCode;
  } catch (error) {
    console.error('Failed to change language:', error);
    // Fallback to default language
    await i18next.changeLanguage(getShortCode(DEFAULT_LANGUAGE));
  }
};

// Get current language information
export const getCurrentLanguageInfo = () => {
  const storedLocale = localStorage.getItem('selectedLocale') as SupportedLocale | null;
  const fullLocale = storedLocale || getFullLocale(i18next.resolvedLanguage || i18next.language || getShortCode(DEFAULT_LANGUAGE));
  return MEDITERRANEAN_LANGUAGES.find(lang => lang.code === fullLocale) || 
         MEDITERRANEAN_LANGUAGES.find(lang => lang.code === DEFAULT_LANGUAGE);
};

// Export functions required by LanguageSwitcher component
export const getAvailableLanguages = () => MEDITERRANEAN_LANGUAGES;

export const getCurrentLanguage = (): SupportedLocale => {
  const storedLocale = localStorage.getItem('selectedLocale') as SupportedLocale | null;
  return storedLocale || getFullLocale(i18next.resolvedLanguage || i18next.language || getShortCode(DEFAULT_LANGUAGE));
};

export const getLanguageConfig = (languageCode: SupportedLocale) => {
  return MEDITERRANEAN_LANGUAGES.find(lang => lang.code === languageCode);
};

// Utility to check if translation exists
export const translationExists = (key: string, namespace?: string): boolean => {
  return i18next.exists(key, { ns: namespace || NAMESPACES.COMMON });
};

// Get translation with fallback
export const getTranslation = (key: string, options?: any): string => {
  const result = i18next.t(key, options);
  return typeof result === 'string' ? result : key;
};

// Load additional namespaces on demand
export const loadNamespaces = async (namespaces: string[]): Promise<void> => {
  try {
    await i18next.loadNamespaces(namespaces);
  } catch (error) {
    console.error('Failed to load namespaces:', error);
  }
};

// Helper to get short language code
export { getShortCode };

// Map short codes back to full locale codes for API calls
export const getFullLocale = (shortCode: string): SupportedLocale => {
  const mapping: Record<string, SupportedLocale> = {
    'en': 'en-GB',
    'it': 'it-IT', 
    'es': 'es-ES',
    'fr': 'fr-FR',
    'de': 'de-DE'
  };
  return mapping[shortCode] || 'en-GB';
};

export default i18next;
